import React, { useEffect, useState } from "react";
import { IField, IMessage } from "../../app/types";
import { useAppSelector } from "../../app/hooks";
import {
  Paper,
  IconButton,
  Grid,
  Tooltip,
  Button,
  Card,
  CardContent,
} from "@mui/material";
import {
  TransitionAlert,
  BreadCrumb,
  Component,
  SwitchComponent,
} from "../Components";
import MenuIcon from "@mui/icons-material/Menu";
import clsx from "clsx";
import { GridRenderCellParams } from "@mui/x-data-grid";
import { setShowHeaders } from "./MainContentUtils";

/* Functional component properties */
interface Props {
  classes: any;
  isOpen: boolean;
  handleDrawerToggle: () => void;
  buttonClickHandler: (btn: IField) => void;
  linkClickHandler: (link: GridRenderCellParams) => void;
}

/* React Functional component */
export const MainContentRenderer: React.FC<Props> = ({
  /* Function arguments (Props) */
  classes,
  isOpen,
  handleDrawerToggle,
  buttonClickHandler,
  linkClickHandler,
}): JSX.Element => {
  /* get state */
  const state = useAppSelector((state) => state);
  const [clear, setClear] = useState<string>("false");
  const gridList: string[] = [];
  const buttonGroup: number[] = [];
  // const [showHeaders, setShowHeaders] = React.useState(
  //   state.mainContent.showHeaders
  // );
  // const gridButtonGroup: number[] = [];

  const handleSwitchChange = () => {
    setShowHeaders();
  };

  useEffect(() => {
    if (
      clear === "false" &&
      state.mainContent.screen !== "RW7000" &&
      state.mainContent.fieldList.find(
        (fld: IField) => fld.name === "REFRESH" && fld.type === "button"
      ) !== undefined
    ) {
      buttonClickHandler(
        state.mainContent.fieldList.find(
          (fld: IField) => fld.name === "REFRESH" && fld.type === "button"
        ) || ({} as IField)
      );
    }
  }, [clear]);

  /* Scroll screen to top when messages are received from the server */
  useEffect(() => {
    if (state.mainContent.messageList.length) window.scrollTo(0, 0);
  }, [state.mainContent.messageList]);

  /* Variable to keep track of blank columns in a row */
  var incr = 0;

  function setIncr(val: number) {
    incr = val;
  }

  return (
    <main
      id="mainContent"
      key={"mainContent"}
      className={clsx(classes.content, {
        [classes.contentShift]: isOpen,
      })}
    >
      {/* To leave space on top to prevent overlapping with Topbar */}
      <div className={classes.toolbar} key={"ToolbarDiv"} />

      <Grid
        container
        spacing={2}
        className={classes.drawerOpenButton}
        key={"SidebarDrawerGridContainer"}
      >
        {/* Icon to open the sidebar drawer */}
        <Grid item xs={12} key={"SidebarDrawerGridItem"}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerToggle}
            edge="start"
            className={clsx(classes.menuButton, isOpen && classes.hide)}
          >
            <MenuIcon />
          </IconButton>
        </Grid>

        {/* Main content header rendering i.e. breadcrumb, caption & alert messages */}
        <Grid item xs={12} key={"BreadCrumbGridItem"}>
          {state.mainContent.navList ? (
            <BreadCrumb
              classes={classes}
              state={state}
              handler={buttonClickHandler}
            />
          ) : (
            ""
          )}

          <Tooltip title={state.mainContent.screen} placement="right" arrow>
            <Button className={classes.caption}>
              {state.mainContent.caption}
            </Button>
          </Tooltip>

          {state.mainContent.messageList.length
            ? state.mainContent.messageList.map((msg: IMessage) => (
                <TransitionAlert classes={classes} item={msg} key={msg.text} />
              ))
            : ""}
        </Grid>

        {/* 
        //TODO
        <Backdrop open={open} className={classes.backdrop}>
          <CircularProgress color="inherit" />
        </Backdrop> */}

        {/* Main content elements rendering */}
        <Grid item xs={12} key={"MainContentGridItem"}>
          <Paper elevation={1}>
            <Grid container spacing={0} key={"MainContentCardGridContainer"}>
              {/* Render switch component to show/hide headers */}
              {state.mainContent.gridList.length ? (
                <SwitchComponent
                  classes={classes}
                  showHeaders={state.mainContent.showHeaders}
                  handleSwitchChange={handleSwitchChange}
                />
              ) : (
                ""
              )}

              <Grid item xs={12} key={"MainContentCardGridItem"}>
                <Card className={classes.cardComponent}>
                  <CardContent>
                    <Grid
                      container
                      spacing={0}
                      key={"MainContentCardContentGridContainer"}
                    >
                      {/* Render the fields (if any) */}
                      {state.mainContent.fieldList.length
                        ? [
                            ...new Set(
                              state.mainContent.fieldList
                                .map((fld) => fld.posRow)
                                .filter((num) => num !== undefined)
                            ),
                          ]
                            // [
                            //     ...Array(
                            //       Math.max(
                            //         ...state.mainContent.fieldList
                            //           .map((fld) => fld.posRow)
                            //           .filter((num) => num !== undefined)
                            //       ) + 1
                            //     ).keys(),
                            //   ]
                            //     .slice(1)
                            .map((posRow: number) => (
                              <Grid
                                container
                                spacing={2}
                                data-idx={setIncr(0)}
                                key={`MainContentRow${posRow}Container`}
                                id={`MainContentRow${posRow}Container`}
                              >
                                {state.mainContent.fieldList.filter(
                                  (fld: IField) =>
                                    // fld.grid === undefined &&
                                    fld.posRow === posRow ||
                                    (fld.grid &&
                                      state.mainContent.gridList.find(
                                        (grid) => grid.posRow === posRow
                                      ))
                                ).length > 0 ? (
                                  state.mainContent.fieldList
                                    .filter(
                                      (fld: IField) =>
                                        // fld.grid === undefined &&
                                        fld.posRow === posRow ||
                                        (fld.grid &&
                                          state.mainContent.gridList.find(
                                            (grid) => grid.posRow === posRow
                                          ))
                                    ) // filter non-grid fields
                                    .map((fld: IField, idx: number) =>
                                      (() => {
                                        switch (true) {
                                          case state.mainContent.fieldList.filter(
                                            (fld) =>
                                              fld.posRow === posRow &&
                                              fld.grid === undefined
                                          ).length >= 1 && // ALL individual buttons, rendering starting from col 1
                                            state.mainContent.fieldList
                                              .filter(
                                                (fld) =>
                                                  fld.posRow === posRow &&
                                                  fld.grid === undefined
                                              )
                                              .find(
                                                (fld) => fld.posCol === 1
                                              ) &&
                                            state.mainContent.fieldList
                                              .filter(
                                                (fld) =>
                                                  fld.posRow === posRow &&
                                                  fld.grid === undefined
                                              )
                                              .find(
                                                (fld) => fld.type !== "button"
                                              ) === undefined:
                                            if (
                                              buttonGroup.indexOf(posRow) < 0 &&
                                              (state.mainContent.showHeaders ||
                                                state.mainContent.gridList
                                                  .length === 0 ||
                                                (!state.mainContent
                                                  .showHeaders &&
                                                  state.mainContent.gridList
                                                    .length &&
                                                  state.mainContent.fieldList.find(
                                                    (field) =>
                                                      field.grid ===
                                                      state.mainContent
                                                        .gridList[0].name
                                                  ) != undefined &&
                                                  fld.posRow >=
                                                    state.mainContent.fieldList.find(
                                                      (field) =>
                                                        field.grid ===
                                                        state.mainContent
                                                          .gridList[0].name
                                                    )!.posRow))
                                            ) {
                                              buttonGroup.push(posRow);
                                              return (
                                                <Grid
                                                  key={fld.posRow + "GridItem"}
                                                  item
                                                  xs={12}
                                                >
                                                  <Component
                                                    key={
                                                      fld.posRow + "Component"
                                                    }
                                                    isButtonGroup={true}
                                                    item={state.mainContent.fieldList.filter(
                                                      (fld) =>
                                                        fld.posRow === posRow &&
                                                        fld.grid === undefined
                                                    )}
                                                    classes={classes}
                                                    clear={clear}
                                                    setClear={setClear}
                                                    buttonClickHandler={
                                                      buttonClickHandler
                                                    }
                                                    linkClickHandler={
                                                      linkClickHandler
                                                    }
                                                  ></Component>
                                                </Grid>
                                              );
                                            } else return "";

                                          // case state.mainContent.fieldList.filter(
                                          //   (fld) =>
                                          //     fld.posRow === posRow &&
                                          //     fld.grid === undefined
                                          // ).length >= 1 &&
                                          //   state.mainContent.fieldList
                                          //     .filter(
                                          //       (fld) =>
                                          //         fld.posRow === posRow &&
                                          //         fld.grid === undefined
                                          //     )
                                          //     .find(
                                          //       (fld) => fld.type !== "button"
                                          //     ) === undefined: // ALL buttons in a row, rendering NOT starting from col 1
                                          //   if (
                                          //     gridButtonGroup.indexOf(posRow) <
                                          //     0
                                          //   ) {
                                          //     gridButtonGroup.push(posRow);
                                          //     return (
                                          //       <Grid
                                          //         key={fld.posRow + "GridItem"}
                                          //         item
                                          //         xs={12}
                                          //       >
                                          //         <Component
                                          //           key={
                                          //             fld.posRow + "Component"
                                          //           }
                                          //           isGridButtonGroup={true}
                                          //           item={state.mainContent.fieldList.filter(
                                          //             (fld) =>
                                          //               fld.posRow === posRow &&
                                          //               fld.grid === undefined
                                          //           )}
                                          //           classes={classes}
                                          //           clear={clear}
                                          //           setClear={setClear}
                                          //           buttonClickHandler={
                                          //             buttonClickHandler
                                          //           }
                                          //           linkClickHandler={
                                          //             linkClickHandler
                                          //           }
                                          //         ></Component>
                                          //       </Grid>
                                          //     );
                                          //   } else return "";

                                          case idx + incr + 1 < fld.posCol &&
                                            !fld.grid:
                                            setIncr(incr + 1);

                                            if (
                                              state.mainContent.showHeaders ||
                                              state.mainContent.gridList
                                                .length === 0 ||
                                              (!state.mainContent.showHeaders &&
                                                state.mainContent.gridList
                                                  .length &&
                                                state.mainContent.fieldList.find(
                                                  (field) =>
                                                    field.grid ===
                                                    state.mainContent
                                                      .gridList[0].name
                                                ) != undefined &&
                                                fld.posRow >=
                                                  state.mainContent.fieldList.find(
                                                    (field) =>
                                                      field.grid ===
                                                      state.mainContent
                                                        .gridList[0].name
                                                  )!.posRow)
                                            )
                                              return (
                                                <React.Fragment
                                                  key={`${fld.posRow}${fld.posCol}ReactFragment`}
                                                >
                                                  {[
                                                    ...Array(
                                                      fld.posCol - (idx + 1)
                                                    ).keys(),
                                                  ].map((idx: number) => (
                                                    /* Render blank spaces */
                                                    <Grid
                                                      key={
                                                        fld.posRow +
                                                        `${fld.posCol}` +
                                                        idx +
                                                        "GridItem"
                                                      }
                                                      id={
                                                        fld.posRow +
                                                        `${fld.posCol}` +
                                                        idx +
                                                        "GridItem"
                                                      }
                                                      item
                                                      idx-incr={setIncr(
                                                        incr + 1
                                                      )}
                                                      xs={
                                                        12 /
                                                        Math.max(
                                                          ...Array.from(
                                                            state.mainContent.fieldList
                                                              .filter(
                                                                (fld: any) =>
                                                                  fld.posRow ===
                                                                    posRow &&
                                                                  fld.grid ===
                                                                    undefined
                                                              )
                                                              .map(
                                                                (fld) =>
                                                                  fld.posCol
                                                              )
                                                          )
                                                        )
                                                      }
                                                    ></Grid>
                                                  ))}
                                                  {/* Render grid component */}
                                                  <Grid
                                                    key={fld.name + "GridItem"}
                                                    id={fld.name + "GridItem"}
                                                    item
                                                    xs={
                                                      12 /
                                                      Math.max(
                                                        ...Array.from(
                                                          state.mainContent.fieldList
                                                            .filter(
                                                              (fld: any) =>
                                                                fld.posRow ===
                                                                  posRow &&
                                                                fld.grid ===
                                                                  undefined
                                                            )
                                                            .map(
                                                              (fld) =>
                                                                fld.posCol
                                                            )
                                                        )
                                                      )
                                                    }
                                                  >
                                                    <Component
                                                      key={
                                                        fld.name + "Component"
                                                      }
                                                      item={fld}
                                                      classes={classes}
                                                      setWidth={
                                                        state.mainContent.fieldList.filter(
                                                          (fld: any) =>
                                                            fld.grid ===
                                                              undefined &&
                                                            fld.posRow ===
                                                              posRow
                                                        ).length > 1
                                                          ? "false"
                                                          : "true"
                                                      }
                                                      clear={clear}
                                                      setClear={setClear}
                                                      buttonClickHandler={
                                                        buttonClickHandler
                                                      }
                                                      linkClickHandler={
                                                        linkClickHandler
                                                      }
                                                    ></Component>
                                                  </Grid>
                                                </React.Fragment>
                                              );
                                            else return "";

                                          default:
                                            if (
                                              fld.grid === undefined ||
                                              (fld.grid &&
                                                gridList.indexOf(fld.grid) < 0)
                                            ) {
                                              if (fld.grid)
                                                gridList.push(fld.grid);

                                              if (
                                                state.mainContent.showHeaders ||
                                                state.mainContent.gridList
                                                  .length === 0 ||
                                                (!state.mainContent
                                                  .showHeaders &&
                                                  state.mainContent.gridList
                                                    .length &&
                                                  state.mainContent.fieldList.find(
                                                    (field) =>
                                                      field.grid ===
                                                      state.mainContent
                                                        .gridList[0].name
                                                  ) != undefined &&
                                                  fld.posRow >=
                                                    state.mainContent.fieldList.find(
                                                      (field) =>
                                                        field.grid ===
                                                        state.mainContent
                                                          .gridList[0].name
                                                    )!.posRow)
                                              )
                                                return (
                                                  <Grid
                                                    key={fld.name + "GridItem"}
                                                    item
                                                    // className={
                                                    //   fld.grid
                                                    //     ? classes.dataGridItem
                                                    //     : ""
                                                    // }
                                                    xs={
                                                      fld.grid
                                                        ? 12
                                                        : 12 /
                                                          Math.max(
                                                            ...Array.from(
                                                              state.mainContent.fieldList
                                                                .filter(
                                                                  (fld: any) =>
                                                                    fld.posRow ===
                                                                      posRow &&
                                                                    fld.grid ===
                                                                      undefined
                                                                  //   &&
                                                                  // fld.type !==
                                                                  //   "button"
                                                                )
                                                                .map(
                                                                  (fld) =>
                                                                    fld.posCol
                                                                )
                                                            )
                                                          )
                                                    }
                                                  >
                                                    <Component
                                                      key={
                                                        fld.grid
                                                          ? fld.grid +
                                                            "Component"
                                                          : fld.name +
                                                            "Component"
                                                      }
                                                      item={
                                                        fld.grid
                                                          ? state.mainContent.gridList.find(
                                                              (grid) =>
                                                                grid.name ===
                                                                fld.grid
                                                            )
                                                          : fld
                                                      }
                                                      classes={classes}
                                                      setWidth={
                                                        state.mainContent.fieldList.filter(
                                                          (fld: any) =>
                                                            fld.grid ===
                                                              undefined &&
                                                            fld.posRow ===
                                                              posRow
                                                          // &&
                                                          // fld.visible === "true"
                                                        ).length > 1
                                                          ? "false"
                                                          : "true"
                                                      }
                                                      clear={clear}
                                                      setClear={setClear}
                                                      buttonClickHandler={
                                                        buttonClickHandler
                                                      }
                                                      linkClickHandler={
                                                        linkClickHandler
                                                      }
                                                    ></Component>
                                                  </Grid>
                                                );
                                              else return "";
                                            }
                                        }
                                      })()
                                    )
                                ) : (
                                  <Grid
                                    key={posRow + "GridItem"}
                                    id={posRow + "GridItem"}
                                    item
                                    xs={12}
                                    className={classes.visuallyHidden}
                                  ></Grid>
                                )}
                              </Grid>
                            ))
                        : ""}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </main>
  );
};
